/* Credentials — handover §14 step 11, and §16's two non-negotiables.
 *
 * **No secret values, ever.** This is an index, not a vault. A record holds a
 * *reference* — which store, which secret name — and the list behind it has no
 * value column at all; the provisioning script fails if one ever appears. So
 * there is nothing on this screen that could leak a secret, because there is
 * nothing here that has one.
 *
 * **There is no reveal, and there never really was.** A button used to fetch the
 * value from Key Vault with the signed-in user's own token. It could not have
 * worked: Key Vault refuses cross-origin browser access outright, by design, so
 * every press errored. Removing it costs nothing — Hird keeps credentials in a
 * password manager, which is where you read one, under that store's own controls
 * and audit.
 *
 * What is left is the part that was always doing the work. The value lives
 * somewhere; this says where, who owns it, who else holds it, when it last
 * changed and when it is next due. That is the 3am question — "who has the
 * FortiGate password and when did it last change" — and it needs no secret to
 * answer.
 *
 * The one finding worth the screen existing: the credentials with no reference
 * at all. Those are being kept somewhere nobody can point at, which is the kind
 * that leaves with the person who knew it.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOnline } from '../../hooks/useOnline';
import { daysUntil, fmtDate } from '../../lib/format';
import { credentialFields, valuesSeen } from '../../lib/registerFields';
import type { ICredential } from '../../models';
import { useOpenRecord } from '../../hooks/useOpenRecord';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Badge,
  Button,
  Controls,
  DataTable,
  EmptyState,
  LoadError,
  Panel,
  SearchBox,
  Segmented,
  StatCard,
  StatGrid,
  TableSkeleton,
  type Column,
} from '../ui';
import { EditDrawer } from './EditDrawer';
import s from './screens.module.scss';
import { PRODUCT } from '../../lib/product';

/** When this credential is next due rotation. Negative means overdue. */
export function rotationDue(c: Pick<ICredential, 'rotated' | 'every'>): string {
  if (!c.rotated) return '';
  const d = new Date(`${c.rotated}T00:00:00`);
  d.setDate(d.getDate() + (c.every || 365));
  return d.toISOString().slice(0, 10);
}

export function CredentialsScreen() {
  const svc = useServices();
  const online = useOnline();
  const qc = useQueryClient();

  const [query, setQuery] = useState('');
  const [lens, setLens] = useState('all');
  const [selected, setSelected] = useState<ICredential | null>(null);
  const [adding, setAdding] = useState(false);

  const credentials = useQuery({ queryKey: qk.credentials, queryFn: () => svc.credentials.list() });

  /* Arriving from a relation elsewhere. The value is whatever the source
     had to hand -- an id where one is stored, a name where it is not --
     so both have to match, and every record gets a URL somebody can send. */
  useOpenRecord(
    credentials.data ?? [],
    (row, value) => row.id === value || row.name.toLowerCase() === value.toLowerCase(),
    setSelected,
  );
  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });

  const close = () => {
    setSelected(null);
    setAdding(false);
  };

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.credentials });
    void qc.invalidateQueries({ queryKey: qk.audit });
    close();
  };

  const save = useMutation({

    meta: { ok: 'Credential saved' },
    mutationFn: (values: Record<string, unknown>) => {
      const patch = {
        ...values,
        every: Number(values.every ?? 365) || 365,
        /* Holders are group names maintained outside this form — carried
           through rather than dropped, because a partial save that blanked them
           would quietly lose who else can get at the thing. */
        holders: selected?.holders ?? [],
      } as unknown as Omit<ICredential, 'id'>;
      return selected ? svc.credentials.update(selected.id, patch) : svc.credentials.create(patch);
    },
    onSuccess: refresh,
  });

  const remove = useMutation({

    meta: { ok: 'Credential deleted' },
    mutationFn: (id: string) => svc.credentials.remove(id),
    onSuccess: refresh,
  });

  const markRotated = useMutation({

    meta: { ok: 'Marked as rotated' },
    mutationFn: (id: string) => svc.credentials.markRotated(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.credentials });
      void qc.invalidateQueries({ queryKey: qk.audit });
    },
  });

  const ownerName = (id: string) =>
    (people.data ?? []).find((p) => p.id === id)?.name ?? (id ? '—' : '');

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (credentials.data ?? [])
      .filter((c) => {
        const due = rotationDue(c);
        if (lens === 'overdue' && !(due && daysUntil(due) < 0)) return false;
        if (lens === 'soon' && !(due && daysUntil(due) >= 0 && daysUntil(due) <= 30)) return false;
        if (lens === 'novault' && c.vault.trim() !== '') return false;
        if (!q) return true;
        return (
          c.name.toLowerCase().includes(q) ||
          c.system.toLowerCase().includes(q) ||
          c.type.toLowerCase().includes(q) ||
          c.holders.some((h) => h.toLowerCase().includes(q))
        );
      })
      .sort((a, b) => (rotationDue(a) || '9999').localeCompare(rotationDue(b) || '9999'));
  }, [credentials.data, query, lens]);

  const totals = useMemo(() => {
    const all = credentials.data ?? [];
    return {
      total: all.length,
      overdue: all.filter((c) => {
        const due = rotationDue(c);
        return due && daysUntil(due) < 0;
      }).length,
      /* A credential with no vault reference is one somebody is keeping
         somewhere else — a note, a password manager, their head. Worth counting,
         because that is the one that goes missing when they leave. */
      unvaulted: all.filter((c) => c.vault.trim() === '').length,
    };
  }, [credentials.data]);

  const columns: Column<ICredential>[] = [
    {
      key: 'name',
      header: 'Credential',
      sortBy: (c) => c.name,
      render: (c) => (
        <div className={s.person}>
          <span className={s.chip}>
            <i className="ti ti-key" aria-hidden />
          </span>
          <div>
            <div className={s.cellMain}>{c.name}</div>
            <div className={s.cellSub}>
              {c.system}
              {c.type ? ` · ${c.type}` : ''}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'owner',
      header: 'Owner',
      sortBy: (c) => ownerName(c.owner),
      render: (c) => ownerName(c.owner) || <span className={s.cellSub}>—</span>,
    },
    {
      key: 'holders',
      header: 'Who else holds it',
      sortBy: (c) => c.holders.length,
      render: (c) =>
        c.holders.length ? (
          <div className={s.tags}>
            {c.holders.map((h) => (
              <Badge key={h} tone="muted">
                {h}
              </Badge>
            ))}
          </div>
        ) : (
          <span className={s.cellSub}>—</span>
        ),
    },
    {
      key: 'rotated',
      header: 'Rotation',
      sortBy: (c) => rotationDue(c),
      render: (c) => {
        const due = rotationDue(c);
        if (!due) return <span className={s.cellSub}>Never rotated</span>;
        const left = daysUntil(due);
        return (
          <>
            <Badge tone={left < 0 ? 'bad' : left <= 30 ? 'warn' : 'ok'}>
              {left < 0 ? `${Math.abs(left)} days overdue` : `Due ${fmtDate(due)}`}
            </Badge>
            <div className={s.cellSub}>
              Last {fmtDate(c.rotated)} · every {c.every} days
            </div>
          </>
        );
      },
    },
    {
      key: 'vault',
      header: 'Vault reference',
      sortBy: (c) => c.vault,
      render: (c) =>
        c.vault ? (
          <span className={s.mono}>{c.vault}</span>
        ) : (
          <Badge tone="warn">Not in a vault</Badge>
        ),
    },
  ];

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Credentials</h1>
          <p>
            What exists, who owns it, who else holds it and when it was last changed. No secret
            values are stored.
          </p>
        </div>
        <Button
          primary
          icon="ti-plus"
          disabled={!online}
          onClick={() => {
            setSelected(null);
            setAdding(true);
          }}
        >
          New credential
        </Button>
      </div>

      {credentials.isError && <LoadError error={credentials.error} what="credentials" />}

      <StatGrid>
        <StatCard
          icon="ti-key"
          label="Credentials"
          value={credentials.isLoading ? '—' : totals.total}
          tone="info"
          foot="On the index"
        />
        <StatCard
          icon="ti-rotate-clockwise"
          label="Overdue rotation"
          value={credentials.isLoading ? '—' : totals.overdue}
          tone={totals.overdue > 0 ? 'bad' : 'ok'}
          foot={totals.overdue > 0 ? 'Past their own interval' : 'All within interval'}
        />
        <StatCard
          icon="ti-lock-open"
          label="Not in a vault"
          value={credentials.isLoading ? '—' : totals.unvaulted}
          tone={totals.unvaulted > 0 ? 'warn' : 'ok'}
          foot={totals.unvaulted > 0 ? 'Held somewhere else' : 'All referenced'}
        />
      </StatGrid>

      <Controls count={`${rows.length} of ${credentials.data?.length ?? 0}`}>
        <SearchBox value={query} onChange={setQuery} placeholder="Credential, system or holder…" />
        <Segmented
          label="Lens"
          value={lens}
          onChange={setLens}
          options={[
            { value: 'all', label: 'All' },
            { value: 'overdue', label: 'Overdue rotation' },
            { value: 'soon', label: 'Due in 30 days' },
            { value: 'novault', label: 'Not in a vault' },
          ]}
        />
      </Controls>

      <Panel flush>
        {credentials.isLoading ? (
          <TableSkeleton />
        ) : (
          <DataTable
            rows={rows}
            columns={columns}
            rowKey={(c) => c.id}
            onRowClick={setSelected}
            empty={
              <EmptyState icon="ti-key" title="Nothing on the index">
                An index of what exists and who owns it. No secret values are stored.
              </EmptyState>
            }
          />
        )}
      </Panel>

      <EditDrawer
        open={selected !== null || adding}
        record={selected}
        fields={credentialFields(valuesSeen(credentials.data, ['system', 'type']))}
        lookups={{ people: people.data ?? [], suppliers: [] }}
        noun="credential"
        icon="ti-key"
        meta={
          selected && (
            <>
              {selected.system && <Badge tone="muted">{selected.system}</Badge>}
              {rotationDue(selected) && (
                <Badge tone={daysUntil(rotationDue(selected)) < 0 ? 'bad' : 'ok'}>
                  Rotation due {fmtDate(rotationDue(selected))}
                </Badge>
              )}
            </>
          )
        }
        onClose={close}
        onSave={(values) => save.mutate(values)}
        onDelete={selected ? () => remove.mutate(selected.id) : undefined}
        saving={save.isPending}
        deleting={remove.isPending}
        error={save.error}
      >
        {selected && (
          <>
            {selected.holders.length > 0 && (
              <div>
                <div className={s.sectionLabel}>Who else holds it</div>
                <div className={s.tags}>
                  {selected.holders.map((h) => (
                    <Badge key={h} tone="muted">
                      {h}
                    </Badge>
                  ))}
                </div>
                <div className={s.hint}>
                  Group membership, maintained in Entra rather than here.
                </div>
              </div>
            )}

            <div>
              <div className={s.sectionLabel}>Where the value is kept</div>

              {!selected.vault ? (
                <div className={s.ownedNote}>
                  <i className="ti ti-lock-open" aria-hidden />
                  <span>
                    Nothing on this record says where this credential is kept, so it is being held
                    somewhere {PRODUCT.name} cannot point at — which is the kind that goes missing when
                    somebody leaves.
                  </span>
                </div>
              ) : (
                <div className={s.ownedNote}>
                  <i className="ti ti-database-off" aria-hidden />
                  <span>
                    Held in <span className={s.mono}>{selected.vault}</span>. {PRODUCT.name} records the
                    reference and never the value — go there to read it, where whoever owns that
                    store decides whether you may and logs that you did.
                  </span>
                </div>
              )}

 <div className={`${s.actions} ${s.spaced}`} >
                <Button
                  icon="ti-rotate-clockwise"
                  disabled={!online || markRotated.isPending}
                  onClick={() => markRotated.mutate(selected.id)}
                >
                  {markRotated.isPending ? 'Recording…' : 'I have just rotated it'}
                </Button>
              </div>
            </div>
          </>
        )}
      </EditDrawer>
    </>
  );
}
